/**
 * جولة أول زيارة — خمس بطاقات تشرح المنتج لمن يفتحه أول مرة.
 *
 * لماذا توجد:
 *   الزائر الجديد يواجه منتجاً يقول له «نطاق سعري» و«قوة الدليل» و«ما نقدر
 *   نعطيك رقماً» — ثلاث عبارات لا تُشرح نفسها. والمحرك يمتنع في أغلب الحالات،
 *   فأول ما يراه كثير من الزوّار هو الامتناع. من لم يُشرَح له الامتناع مسبقاً
 *   يقرؤه عطلاً في الموقع ويخرج.
 *
 * قواعد التصميم:
 *   ١. تظهر مرة واحدة — على الرئيسية وحدها — ثم لا تعود إلا بطلب صريح.
 *   ٢. لا تحمل رقم بيانات واحداً. الأرقام تُقرأ من الشاشات نفسها، والنص الذي
 *      يحمل رقماً منسوخاً يشيخ بصمت.
 *   ٣. تنتهي بفعل لا بزر «تم» — الشرح الذي لا يُتبع بتجربة يُنسى.
 *   ٤. التركوازي على زر الفعل ومؤشر الخطوة فقط. لا طلاء.
 *
 * لا منطق مجال هنا — عرض ونصّ فقط.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { color } from '@/design/tokens';
import { useJourney } from './journey';

/** مفتاح «رآها» — نفس نمط `nitaq.map.city`: تفضيل زائر لا بيانات. */
export const GUIDE_SEEN_KEY = 'nitaq.guide.seen';

/**
 * القراءة والكتابة محاطتان بحماية: المتصفح في وضع خاص قد يمنع التخزين.
 * وحين يُمنع، الافتراض «رآها» — إظهار طبقة لا يمكن إسكاتها أسوأ من إخفائها.
 */
function hasSeenGuide(): boolean {
  try {
    return window.localStorage.getItem(GUIDE_SEEN_KEY) === '1';
  } catch {
    return true;
  }
}

export function markGuideSeen(): void {
  try {
    window.localStorage.setItem(GUIDE_SEEN_KEY, '1');
  } catch {
    /* التخزين ممنوع — تُغلق الجولة لهذه الجلسة ولا نُفشل الصفحة. */
  }
}

/** يُستدعى من صفحة «كيف يعمل نطاق» حين يطلب الزائر إعادة الجولة. */
export function replayGuideTour(): void {
  try {
    window.localStorage.removeItem(GUIDE_SEEN_KEY);
  } catch {
    /* لا شيء — الزر لا يَعِد بأكثر مما يستطيع. */
  }
}

// --------------------------------------------------------------- المحتوى

interface TourStep {
  /** ختم الورقة — يبقى ثابتاً كي تُقرأ البطاقات ورقةً واحدة. */
  title: string;
  lead: string;
  body: ReactNode;
}

/** سطر داخل بطاقة — عنوان قصير ثم شرحه. */
function Line({ term, children }: { term: string; children: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        paddingBlock: 10,
        borderTop: `1px solid ${color.hairlineSoft}`,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: color.text }}>{term}</div>
      <div style={{ marginTop: 3, fontSize: 12.5, lineHeight: 1.85, color: color.textSubtle }}>
        {children}
      </div>
    </div>
  );
}

const STEPS: TourStep[] = [
  {
    title: 'وش يسوّي نطاق؟',
    lead: 'يجاوب سؤالاً واحداً: هل السعر المطلوب يطابق السوق — ووش الدليل؟',
    body: (
      <p style={{ fontSize: 13, lineHeight: 1.95, color: color.textSubtle }}>
        تعطينا الحي والمساحة والسعر المطلوب، فنقارن القطعة بالصفقات المسجّلة في حيّها ونعرض
        ما تثبته البيانات — لا أكثر.
      </p>
    ),
  },
  {
    title: 'من وين الأرقام؟',
    lead: 'من صفقات موثّقة رسمياً — بيع تمّ فعلاً وانتقلت ملكيته.',
    body: (
      <p style={{ fontSize: 13, lineHeight: 1.95, color: color.textSubtle }}>
        الإعلانات سعر <strong style={{ color: color.textBody }}>طلب</strong> لا سعر{' '}
        <strong style={{ color: color.textBody }}>تنفيذ</strong>، ولذلك لا تدخل في حساب النطاق
        السعري إطلاقاً.
      </p>
    ),
  },
  {
    title: 'وش تشوف في النتيجة؟',
    lead: 'ثلاثة أشياء، وكل واحد يجاوب سؤالاً مختلفاً.',
    body: (
      <div>
        <Line term="النطاق السعري للسوق">حدّان يقع بينهما سعر السوق لقطعة مثل قطعتك.</Line>
        <Line term="جملة الحكم">
          سطر واحد مباشر: «السعر المطلوب أقل من نطاق السوق بـ&nbsp;٨٪».
        </Line>
        <Line term="قوة الدليل">
          قوي أو متوسط أو ضعيف — ومعها عدد الصفقات المشابهة التي بُني عليها الرقم.
        </Line>
      </div>
    ),
  },
  {
    title: 'ومتى ما نعطيك رقماً؟',
    lead: 'أحياناً نقول: ما نقدر نعطيك نطاقاً موثوقاً لهذي القطعة.',
    body: (
      <div>
        <p style={{ fontSize: 13, lineHeight: 1.95, color: color.textSubtle }}>
          ونعرض السبب برقم — كم صفقة وُجدت في الحي خلال المدة. الحي الذي صفقاته قليلة لا
          يُبنى عليه رقم يُوثق به.
        </p>
        <p
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: `1px solid ${color.hairlineSoft}`,
            fontSize: 13,
            lineHeight: 1.95,
            color: color.textBody,
          }}
        >
          نطاق يمتنع بدل ما يخمّن — <strong>والامتناع نفسه معلومة</strong>.
        </p>
      </div>
    ),
  },
  {
    title: 'خلّنا نمشيها سوا',
    lead: 'بدل ما نشرح أكثر — نمشي معك على قطعة حقيقية خطوة خطوة.',
    body: (
      <p style={{ fontSize: 13, lineHeight: 1.95, color: color.textSubtle }}>
        نفس شاشات الموقع، ومعك في كل خطوة سطر يقول لماذا نسأل، وقيمة جاهزة بضغطة إن ما حبيت
        تكتب. وفي النهاية نشرح لك النتيجة وهي أمامك. تقدر توقفها في أي لحظة.
      </p>
    ),
  },
];

// ---------------------------------------------------------------- المكوّن

export function FirstVisitTour(): JSX.Element | null {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const journey = useJourney();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  /**
   * الفتح مربوط بالمسار لا بأول تركيب:
   * الزائر قد يهبط على صفحة داخلية من نتيجة بحث، فلا نعترضه هناك. وإعادة
   * العرض من صفحة «كيف يعمل نطاق» تمرّ بالرئيسية فيلتقطها هذا الأثر نفسه.
   */
  useEffect(() => {
    if (pathname !== '/') {
      setOpen(false);
      return;
    }
    if (hasSeenGuide()) return;
    setIndex(0);
    setOpen(true);
  }, [pathname]);

  const close = useCallback(() => {
    markGuideSeen();
    setOpen(false);
  }, []);

  /** Escape يغلق — طبقة لا تُغلق بلوحة المفاتيح حبسٌ لا إرشاد. */
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') close();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open) return null;

  const step = STEPS[index]!;
  const isLast = index === STEPS.length - 1;

  /** يبدأ الرحلة على الشاشات الفعلية — الجولة تنتقل من الشرح إلى التجربة. */
  function startJourney(): void {
    close();
    journey.start();
    navigate('/evaluate');
  }

  function goToGuide(): void {
    close();
    navigate('/guide');
  }

  return (
    <div
      className="nq-overlay"
      data-testid="first-visit-tour"
      role="dialog"
      aria-modal="true"
      aria-label="مرشد نطاق"
    >
      <div className="nq-guide nq-enter">
        <div
          className="nq-grid nq-guide__head"
          style={{ borderBottom: `1px solid ${color.hairlineSoft}` }}
        >
          <span className="nq-scheme">NITAQ / GUIDE</span>
          <h2 style={{ marginTop: 10, fontSize: 21, fontWeight: 500, lineHeight: 1.4 }}>
            {step.title}
          </h2>
          <p style={{ marginTop: 8, fontSize: 13.5, lineHeight: 1.9, color: color.textBody }}>
            {step.lead}
          </p>
        </div>

        <div style={{ paddingBlock: 16, minHeight: 118 }}>{step.body}</div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            paddingTop: 16,
            borderTop: `1px solid ${color.hairlineSoft}`,
          }}
        >
          <div className="nq-guide__dots" data-testid="tour-progress" aria-hidden="true">
            {STEPS.map((s, i) => (
              <span
                key={s.title}
                className={i === index ? 'nq-guide__dot nq-guide__dot--on' : 'nq-guide__dot'}
              />
            ))}
          </div>
          <span className="nq-num nq-micro" dir="ltr">
            {index + 1} / {STEPS.length}
          </span>

          <span style={{ flex: 1 }} />

          {isLast ? (
            <>
              <button type="button" className="nq-btn nq-btn--ghost nq-btn--sm" onClick={goToGuide}>
                كيف يعمل نطاق
              </button>
              <button type="button" className="nq-btn nq-btn--primary" onClick={startJourney}>
                ابدأ الجولة
              </button>
            </>
          ) : (
            <>
              <button type="button" className="nq-btn nq-btn--quiet nq-btn--sm" onClick={close}>
                تخطّي
              </button>
              {index > 0 ? (
                <button
                  type="button"
                  className="nq-btn nq-btn--ghost nq-btn--sm"
                  onClick={() => setIndex((i) => i - 1)}
                >
                  رجوع
                </button>
              ) : null}
              <button
                type="button"
                className="nq-btn nq-btn--primary"
                onClick={() => setIndex((i) => i + 1)}
              >
                التالي
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
